import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  ArrowLeft, 
  ArrowRight, 
  Send, 
  Check, 
  DollarSign,
  Globe,
  LogOut,
  User,
  CreditCard,
  Info
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const corridors = [
  { id: 'us-mx', from: 'US', to: 'Mexico', fromCurrency: 'USD', toCurrency: 'MXN', flag: '\ud83c\uddfa\ud83c\uddf8 \u2192 \ud83c\uddf2\ud83c\uddfd', rate: 17.5 },
  { id: 'us-ph', from: 'US', to: 'Philippines', fromCurrency: 'USD', toCurrency: 'PHP', flag: '\ud83c\uddfa\ud83c\uddf8 \u2192 \ud83c\uddf5\ud83c\udded', rate: 56.2 },
  { id: 'eu-ng', from: 'EU', to: 'Nigeria', fromCurrency: 'EUR', toCurrency: 'NGN', flag: '\ud83c\uddea\ud83c\uddfa \u2192 \ud83c\uddf3\ud83c\uddec', rate: 920.0 }
];

const EnhancedTransferPage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [recipients, setRecipients] = useState([]);
  const [loadingRecipients, setLoadingRecipients] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [transferData, setTransferData] = useState({
    amount: '',
    corridorId: 'us-mx',
    recipientId: '',
    recipientName: '',
    recipientAccount: '',
    useExistingRecipient: true
  });

  const [feePreview, setFeePreview] = useState(null);

  useEffect(() => {
    loadRecipients();
  }, []);

  useEffect(() => {
    if (transferData.amount && parseFloat(transferData.amount) > 0) {
      calculateFees();
    }
  }, [transferData.amount, transferData.corridorId]);

  const loadRecipients = async () => {
    try {
      const response = await axios.get(`${API}/recipients?user_id=${user.id}`);
      setRecipients(response.data);
    } catch (error) {
      console.error('Failed to load recipients:', error);
    } finally {
      setLoadingRecipients(false);
    }
  };

  const calculateFees = async () => {
    try {
      const amount = parseFloat(transferData.amount);
      const response = await axios.get(`${API}/calculate-fee?amount=${amount}`);
      setFeePreview(response.data);
    } catch (error) {
      console.error('Failed to calculate fees:', error);
    }
  };

  const selectedCorridor = corridors.find(c => c.id === transferData.corridorId);
  const selectedRecipient = recipients.find(r => r.id === transferData.recipientId);

  const canProceedStep1 = transferData.amount && parseFloat(transferData.amount) > 0;
  const canProceedStep2 = transferData.useExistingRecipient 
    ? transferData.recipientId 
    : (transferData.recipientName && transferData.recipientAccount);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const transactionData = {
        amount: parseFloat(transferData.amount),
        from_currency: selectedCorridor.fromCurrency,
        to_currency: selectedCorridor.toCurrency,
        from_country: selectedCorridor.from,
        to_country: selectedCorridor.to,
        recipient_name: transferData.useExistingRecipient 
          ? selectedRecipient.name 
          : transferData.recipientName,
        recipient_account: transferData.useExistingRecipient 
          ? selectedRecipient.account_number 
          : transferData.recipientAccount
      };

      await axios.post(`${API}/transactions?user_id=${user.id}`, transactionData);
      toast.success('Transfer initiated successfully!');
      navigate('/transactions');
    } catch (error) {
      toast.error('Transfer failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const calculateReceiveAmount = () => {
    if (!transferData.amount || !selectedCorridor) return 0;
    const amount = parseFloat(transferData.amount);
    return (amount * selectedCorridor.rate).toFixed(2);
  };

  return (
    <div className='min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50'>
      {/* Navigation */}
      <nav className='bg-white border-b shadow-sm'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='flex justify-between items-center h-16'>
            <div className='flex items-center space-x-4'>
              <Button variant='ghost' size='icon' onClick={() => navigate('/dashboard')}>
                <ArrowLeft className='w-5 h-5' />
              </Button>
              <div className='flex items-center space-x-3'>
                <img 
                  src='https://customer-assets.emergentagent.com/job_smart-money-route/artifacts/6teoyzj7_Generated%20Image%20November%2018%2C%202025%20-%203_06PM.png' 
                  alt='RouteFlow AI' 
                  className='h-10 w-auto'
                />
                <span className='text-xl font-bold gradient-text'>Send Money</span>
              </div>
            </div>
            <Button variant='ghost' onClick={onLogout}>
              <LogOut className='w-4 h-4 mr-2' /> Logout
            </Button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className='max-w-4xl mx-auto px-4 py-8'>
        {/* Progress Steps */}
        <div className='mb-8'>
          <div className='flex items-center justify-center space-x-4'>
            {[1, 2, 3].map((s) => (
              <div key={s} className='flex items-center'>
                <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                  step >= s 
                    ? 'bg-emerald-500 border-emerald-500 text-white' 
                    : 'bg-white border-gray-300 text-gray-400'
                }`}>
                  {step > s ? <Check className='w-5 h-5' /> : s}
                </div>
                {s < 3 && (
                  <div className={`w-16 h-1 ${step > s ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                )}
              </div>
            ))}
          </div>
          <div className='flex justify-between mt-3 text-sm text-gray-600'>
            <span className={step === 1 ? 'font-semibold text-emerald-600' : ''}>Amount</span>
            <span className={step === 2 ? 'font-semibold text-emerald-600' : ''}>Recipient</span>
            <span className={step === 3 ? 'font-semibold text-emerald-600' : ''}>Confirm</span>
          </div>
        </div>

        {/* Step 1: Amount & Corridor */}
        {step === 1 && (
          <Card className='border-0 shadow-lg'>
            <CardHeader>
              <CardTitle className='flex items-center space-x-2'>
                <DollarSign className='w-6 h-6 text-emerald-600' />
                <span>Enter Transfer Amount</span>
              </CardTitle>
              <CardDescription>Choose how much you want to send</CardDescription>
            </CardHeader>
            <CardContent className='space-y-6'>
              {/* Corridor Selection */}
              <div className='space-y-2'>
                <Label>Transfer Route</Label>
                <Select 
                  value={transferData.corridorId} 
                  onValueChange={(value) => setTransferData({ ...transferData, corridorId: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {corridors.map((corridor) => (
                      <SelectItem key={corridor.id} value={corridor.id}>
                        {corridor.flag} {corridor.from} → {corridor.to}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Amount Input */}
              <div className='space-y-2'>
                <Label htmlFor='amount'>You Send</Label>
                <div className='relative'>
                  <span className='absolute left-3 top-1/2 -translate-y-1/2 text-gray-500'>
                    {selectedCorridor.fromCurrency}
                  </span>
                  <Input
                    id='amount'
                    type='number'
                    step='0.01'
                    placeholder='0.00'
                    value={transferData.amount}
                    onChange={(e) => setTransferData({ ...transferData, amount: e.target.value })}
                    className='pl-16 text-2xl h-16 font-semibold'
                  />
                </div>
              </div>

              {/* Live Fee Preview */}
              {feePreview && (
                <div className='bg-gray-50 rounded-lg p-4 space-y-2'>
                  <div className='flex justify-between text-sm'>
                    <span className='text-gray-600'>Transfer Amount</span>
                    <span className='font-semibold'>{formatCurrency(feePreview.amount)}</span>
                  </div>
                  <div className='flex justify-between text-sm'>
                    <span className='text-gray-600'>Service Fee ({feePreview.fee_percentage}%)</span>
                    <span className='font-semibold text-emerald-600'>{formatCurrency(feePreview.service_fee)}</span>
                  </div>
                  <div className='flex justify-between text-sm'>
                    <span className='text-gray-600'>Network Fee (estimated)</span>
                    <span className='font-semibold'>{formatCurrency(2.40)}</span>
                  </div>
                  <div className='border-t pt-2 flex justify-between'>
                    <span className='font-semibold'>Total You Pay</span>
                    <span className='font-bold text-lg'>{formatCurrency(feePreview.total_amount + 2.40)}</span>
                  </div>
                  <div className='bg-emerald-50 border border-emerald-200 rounded p-3 mt-3'>
                    <div className='flex justify-between items-center'>
                      <span className='text-emerald-900 font-semibold'>Recipient Gets</span>
                      <div className='text-right'>
                        <p className='text-2xl font-bold text-emerald-900'>
                          {calculateReceiveAmount()} {selectedCorridor.toCurrency}
                        </p>
                        <p className='text-sm text-emerald-700'>
                          Rate: 1 {selectedCorridor.fromCurrency} = {selectedCorridor.rate} {selectedCorridor.toCurrency}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <Button 
                className='w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700'
                onClick={() => setStep(2)}
                disabled={!canProceedStep1}
              >
                Continue <ArrowRight className='w-4 h-4 ml-2' />
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Recipient Selection */}
        {step === 2 && (
          <Card className='border-0 shadow-lg'>
            <CardHeader>
              <CardTitle className='flex items-center space-x-2'>
                <User className='w-6 h-6 text-emerald-600' />
                <span>Select Recipient</span>
              </CardTitle>
              <CardDescription>Who are you sending money to?</CardDescription>
            </CardHeader>
            <CardContent className='space-y-6'>
              {/* Toggle between existing and new recipient */}
              <div className='flex space-x-2'>
                <Button
                  variant={transferData.useExistingRecipient ? 'default' : 'outline'}
                  onClick={() => setTransferData({ ...transferData, useExistingRecipient: true })}
                  className='flex-1'
                >
                  Saved Recipients
                </Button>
                <Button
                  variant={!transferData.useExistingRecipient ? 'default' : 'outline'}
                  onClick={() => setTransferData({ ...transferData, useExistingRecipient: false })}
                  className='flex-1'
                >
                  New Recipient
                </Button>
              </div>

              {transferData.useExistingRecipient ? (
                // Existing Recipients
                loadingRecipients ? (
                  <div className='flex justify-center py-8'>
                    <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600'></div>
                  </div>
                ) : recipients.length === 0 ? (
                  <div className='text-center py-8 text-gray-500'>
                    <User className='w-12 h-12 mx-auto mb-2 opacity-50' />
                    <p>No saved recipients yet</p>
                    <Button 
                      variant='link' 
                      onClick={() => setTransferData({ ...transferData, useExistingRecipient: false })}
                    >
                      Add a new recipient
                    </Button>
                  </div>
                ) : (
                  <div className='space-y-3'>
                    {recipients.map((recipient) => (
                      <div
                        key={recipient.id}
                        className={`p-4 border-2 rounded-lg cursor-pointer hover:border-emerald-500 transition ${
                          transferData.recipientId === recipient.id 
                            ? 'border-emerald-500 bg-emerald-50' 
                            : 'border-gray-200'
                        }`}
                        onClick={() => setTransferData({ ...transferData, recipientId: recipient.id })}
                      >
                        <div className='flex justify-between items-center'>
                          <div>
                            <p className='font-semibold'>{recipient.name}</p>
                            <p className='text-sm text-gray-600'>{recipient.country}</p>
                            <p className='text-sm text-gray-500'>{recipient.account_number}</p>
                          </div>
                          {transferData.recipientId === recipient.id && (
                            <Check className='w-6 h-6 text-emerald-600' />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )
              ) : (
                // New Recipient
                <div className='space-y-4'>
                  <div className='space-y-2'>
                    <Label htmlFor='recipient-name'>Recipient Name</Label>
                    <Input
                      id='recipient-name'
                      placeholder='John Doe'
                      value={transferData.recipientName}
                      onChange={(e) => setTransferData({ ...transferData, recipientName: e.target.value })}
                    />
                  </div>
                  <div className='space-y-2'>
                    <Label htmlFor='recipient-account'>Account Number / Mobile Number</Label>
                    <Input
                      id='recipient-account'
                      placeholder='1234567890'
                      value={transferData.recipientAccount}
                      onChange={(e) => setTransferData({ ...transferData, recipientAccount: e.target.value })}
                    />
                  </div>
                </div>
              )}

              <div className='flex space-x-3'>
                <Button
                  variant='outline'
                  className='flex-1'
                  onClick={() => setStep(1)}
                >
                  <ArrowLeft className='w-4 h-4 mr-2' /> Back
                </Button>
                <Button 
                  className='flex-1 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700'
                  onClick={() => setStep(3)}
                  disabled={!canProceedStep2}
                >
                  Continue <ArrowRight className='w-4 h-4 ml-2' />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Review & Confirm */}
        {step === 3 && (
          <Card className='border-0 shadow-lg'>
            <CardHeader>
              <CardTitle className='flex items-center space-x-2'>
                <Check className='w-6 h-6 text-emerald-600' />
                <span>Review & Confirm</span>
              </CardTitle>
              <CardDescription>Please review your transfer details</CardDescription>
            </CardHeader>
            <CardContent className='space-y-6'>
              {/* Transfer Summary */}
              <div className='bg-gradient-to-br from-emerald-500 to-teal-600 text-white rounded-lg p-6'>
                <p className='text-emerald-100 text-sm mb-2'>You are sending</p>
                <p className='text-4xl font-bold mb-4'>{formatCurrency(parseFloat(transferData.amount))}</p>
                <div className='flex items-center justify-between'>
                  <div>
                    <p className='text-emerald-100 text-sm'>Recipient gets</p>
                    <p className='text-2xl font-semibold'>{calculateReceiveAmount()} {selectedCorridor.toCurrency}</p>
                  </div>
                  <div className='text-right'>
                    <p className='text-emerald-100 text-sm'>Exchange rate</p>
                    <p className='text-lg'>1 {selectedCorridor.fromCurrency} = {selectedCorridor.rate} {selectedCorridor.toCurrency}</p>
                  </div>
                </div>
              </div>

              {/* Details */}
              <div className='space-y-4'>
                <div>
                  <p className='text-sm text-gray-600'>Transfer Route</p>
                  <p className='font-semibold'>{selectedCorridor.flag} {selectedCorridor.from} → {selectedCorridor.to}</p>
                </div>
                <div>
                  <p className='text-sm text-gray-600'>Recipient</p>
                  <p className='font-semibold'>
                    {transferData.useExistingRecipient ? selectedRecipient?.name : transferData.recipientName}
                  </p>
                  <p className='text-sm text-gray-500'>
                    {transferData.useExistingRecipient ? selectedRecipient?.account_number : transferData.recipientAccount}
                  </p>
                </div>
                {feePreview && (
                  <div>
                    <p className='text-sm text-gray-600 mb-2'>Fee Breakdown</p>
                    <div className='bg-gray-50 rounded-lg p-3 space-y-1 text-sm'>
                      <div className='flex justify-between'>
                        <span>Service Fee</span>
                        <span>{formatCurrency(feePreview.service_fee)}</span>
                      </div>
                      <div className='flex justify-between'>
                        <span>Network Fee</span>
                        <span>{formatCurrency(2.40)}</span>
                      </div>
                      <div className='flex justify-between font-semibold pt-1 border-t'>
                        <span>Total Fees</span>
                        <span>{formatCurrency(feePreview.service_fee + 2.40)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className='flex space-x-3'>
                <Button
                  variant='outline'
                  className='flex-1'
                  onClick={() => setStep(2)}
                  disabled={submitting}
                >
                  <ArrowLeft className='w-4 h-4 mr-2' /> Back
                </Button>
                <Button 
                  className='flex-1 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700'
                  onClick={handleSubmit}
                  disabled={submitting}
                >
                  {submitting ? (
                    <>Processing...</>
                  ) : (
                    <>
                      <Send className='w-4 h-4 mr-2' /> Send Money
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default EnhancedTransferPage;
